import React, { useState, useEffect } from 'react';
import { getDashboardStats } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import {
  ChartBar, Warning, Lightning, Clock, CheckCircle, ArrowRight
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  'New Idea Repository': '#C4B5FD',
  'Backlog': '#E8E8E8',
  'In Discussion': '#FFE7B3',
  'In Design': '#D8B4E2',
  'In Development': '#B3D4FF',
  'In QA': '#FFC2B3',
  'Blocked': '#FF3B30',
  'Completed': '#34C759',
  'Implemented': '#10B981',
};

const PRIORITY_COLORS = { High: '#FF3B30', Medium: '#FFB800', Low: '#34C759' };

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getDashboardStats()
      .then(r => { setStats(r.data); setLoading(false); })
      .catch(err => { console.error('Dashboard stats failed:', err.message); setLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-8 text-[#555555]" data-testid="dashboard-loading">Loading dashboard...</div>;
  if (!stats) return <div className="p-8 text-[#555555]">Failed to load dashboard</div>;

  const statusEntries = Object.entries(stats.by_status || {});
  const priorityEntries = Object.entries(stats.by_priority || {});
  const teamEntries = Object.entries(stats.by_team || {});
  const brandEntries = Object.entries(stats.by_brand || {});
  const maxStatusCount = Math.max(...statusEntries.map(([,v]) => v), 1);

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-bold tracking-tight text-[#0F0F0F]">Dashboard</h1>
        <p className="text-sm text-[#555555] mt-1">Overview of all tasks across teams and brands</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={ChartBar} label="Total Tasks" value={stats.total} color="#32B4AC" testId="stat-total" />
        <StatCard icon={Warning} label="Overdue" value={stats.overdue} color="#FF3B30" testId="stat-overdue" />
        <StatCard icon={Lightning} label="High Priority" value={stats.by_priority?.High || 0} color="#FFB800" testId="stat-high-priority" />
        <StatCard icon={CheckCircle} label="Completed" value={(stats.by_status?.Completed || 0) + (stats.by_status?.Implemented || 0)} color="#34C759" testId="stat-completed" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Status */}
        <div className="border border-[#E8E8E8] rounded-md p-5" data-testid="chart-by-status">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-4">Tasks by Status</h3>
          <div className="space-y-3">
            {statusEntries.map(([status, count]) => (
              <div key={status} className="flex items-center gap-3">
                <span className="w-28 text-xs text-[#1A1A1A] truncate">{status}</span>
                <div className="flex-1 h-5 bg-[#F4F4F4] rounded-md overflow-hidden">
                  <div
                    className="h-full rounded-md transition-all duration-500"
                    style={{ width: `${(count / maxStatusCount) * 100}%`, backgroundColor: STATUS_COLORS[status] || '#E8E8E8' }}
                  />
                </div>
                <span className="w-8 text-right text-xs font-semibold text-[#0F0F0F]">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By Priority */}
        <div className="border border-[#E8E8E8] rounded-md p-5" data-testid="chart-by-priority">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-4">Tasks by Priority</h3>
          <div className="flex items-end gap-6 h-40 pt-4">
            {priorityEntries.map(([p, count]) => {
              const maxP = Math.max(...priorityEntries.map(([,v]) => v), 1);
              return (
                <div key={p} className="flex-1 flex flex-col items-center gap-2">
                  <span className="text-sm font-bold text-[#0F0F0F]">{count}</span>
                  <div className="w-full rounded-md" style={{ height: `${(count / maxP) * 100}%`, minHeight: '4px', backgroundColor: PRIORITY_COLORS[p] }} />
                  <span className="text-xs text-[#555555] font-medium">{p}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Team & Brand row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-[#E8E8E8] rounded-md p-5" data-testid="chart-by-team">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-4">Tasks by Team</h3>
          <div className="space-y-2">
            {teamEntries.map(([team, count]) => (
              <div key={team} className="flex items-center justify-between py-1.5 border-b border-[#F4F4F4] last:border-0">
                <span className="text-sm text-[#1A1A1A]">{team}</span>
                <span className="text-sm font-semibold text-[#0F0F0F] bg-[#F4F4F4] px-2 py-0.5 rounded-full">{count}</span>
              </div>
            ))}
            {teamEntries.length === 0 && <p className="text-xs text-[#555555]">No teams assigned yet</p>}
          </div>
        </div>

        <div className="border border-[#E8E8E8] rounded-md p-5" data-testid="chart-by-brand">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-4">Tasks by Brand</h3>
          <div className="space-y-2">
            {brandEntries.map(([brand, count]) => (
              <div key={brand} className="flex items-center justify-between py-1.5 border-b border-[#F4F4F4] last:border-0">
                <span className="text-sm text-[#1A1A1A]">{brand}</span>
                <span className="text-sm font-semibold text-[#0F0F0F] bg-[#F4F4F4] px-2 py-0.5 rounded-full">{count}</span>
              </div>
            ))}
            {brandEntries.length === 0 && <p className="text-xs text-[#555555]">No brands assigned yet</p>}
          </div>
        </div>
      </div>

      {/* Recent tasks */}
      <div className="border border-[#E8E8E8] rounded-md p-5" data-testid="recent-tasks">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-4">Recently Updated</h3>
        <div className="space-y-1">
          {(stats.recent || []).map(t => (
            <button
              key={t.id}
              onClick={() => navigate(`/tasks/${t.id}`)}
              className="w-full flex items-center justify-between py-2.5 px-3 hover:bg-[#F4F4F4] rounded-md transition-colors duration-150 text-left group"
              data-testid={`recent-task-${t.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLORS[t.status] || '#E8E8E8' }} />
                <span className="text-sm text-[#0F0F0F] truncate">{t.title}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: PRIORITY_COLORS[t.priority] + '20', color: PRIORITY_COLORS[t.priority] }}>{t.priority}</span>
                <ArrowRight size={14} className="text-[#555555] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          ))}
          {(stats.recent || []).length === 0 && <p className="text-xs text-[#555555] py-2">No tasks yet</p>}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="border border-[#E8E8E8] rounded-md p-4 hover:border-[#1A1A1A] transition-colors duration-150" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} style={{ color }} />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">{label}</span>
      </div>
      <p className="font-['Manrope'] text-2xl font-bold text-[#0F0F0F]">{value}</p>
    </div>
  );
}
